"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Cookie } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

const COOKIE_CONSENT_KEY = "cookie-consent"

type ConsentStatus = "accepted" | "rejected" | null

export function CookieConsent() {
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  const [showBanner, setShowBanner] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus
    
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        setShowBanner(true)
        setIsVisible(true)
      }, 1000)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted")
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
    
    // Here you can initialize analytics, tracking, etc.
    // Example: initializeGoogleAnalytics()
  }

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected")
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
  }

  if (!showBanner) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
          suppressHydrationWarning
        >
          <div className="container mx-auto max-w-6xl">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-200 p-4 sm:p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6" suppressHydrationWarning>
              {/* Icon */}
              <div className="flex-shrink-0" suppressHydrationWarning>
                <div className="w-12 h-12 rounded-full bg-[rgb(3,74,166)]/10 flex items-center justify-center" suppressHydrationWarning>
                  <Cookie className="h-6 w-6 text-[rgb(3,74,166)]" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1" suppressHydrationWarning>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2 font-mono">
                  {t.cookieConsent.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3">
                  {t.cookieConsent.description}{" "}
                  <Link 
                    href={`/${currentLocale}/${currentLocale === 'tr' ? 'cerez-politikasi' : 'cookie-policy'}`}
                    className="text-[rgb(3,74,166)] hover:text-[rgb(2,40,89)] underline font-medium"
                  >
                    {t.cookieConsent.cookiePolicyLink}
                  </Link>
                  {" "}{currentLocale === 'tr' ? 'sayfamızı ziyaret edebilirsiniz.' : 'page.'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" suppressHydrationWarning>
                <Button
                  onClick={handleReject}
                  variant="outline"
                  className="w-full sm:w-auto border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  {t.cookieConsent.reject}
                </Button>
                <Button
                  onClick={handleAccept}
                  className="w-full sm:w-auto bg-[rgb(3,74,166)] hover:bg-[rgb(2,40,89)] text-white font-medium"
                >
                  {t.cookieConsent.accept}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

